import { useState, useEffect, useRef } from "react";
import { Icon } from "@iconify/react";
import Swal from "sweetalert2";
import Pagination from "../Pagination";

/*  Helper: format comma */
function formatWithCommas(numStr) {
  if (numStr === null || numStr === undefined || numStr === "") return "";
  const parts = numStr.toString().split(".");
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return parts.join(".");
  
}

const IntegerInput = ({ value, onValueChange }) => {
  const [localValue, setLocalValue] = useState("");

  useEffect(() => {
    if (value === null || value === undefined || value === "") {
      setLocalValue("");
    } else {
      setLocalValue(String(value));
    }
  }, [value]);

  const handleFocus = (e) => e.target.select();

  const handleChange = (e) => {
    let raw = e.target.value.replace(/\D/g, ""); //  เอาเฉพาะตัวเลข

    //  ไม่ให้เป็น 0
    if (raw === "0") raw = "";

    setLocalValue(raw);
    onValueChange(raw ? parseInt(raw) : "");
  };

  const handleBlur = () => {
    if (!localValue) {
      setLocalValue("1");        //  บังคับให้เป็น 1 ถ้าว่าง
      onValueChange(1);
    }
  };

  return (
    <input
      type="text"
      value={localValue}
      onFocus={handleFocus}
      onChange={handleChange}
      onBlur={handleBlur}
      className="w-full h-[42px] border border-gray-300 rounded-md px-3 py-1 text-right text-[14px] focus:ring-1 focus:ring-[#E13F57] focus:border-[#E13F57]"
      inputMode="numeric"
    />
  );
};

/*  NumberInput เหมือน ContractPaymentTable */
const NumberInput = ({ value, onValueChange }) => {
  const [localValue, setLocalValue] = useState("");
  const inputRef = useRef(null);

  useEffect(() => {
    if (document.activeElement !== inputRef.current) {
      if (value === null || value === undefined || value === "") {
        setLocalValue("");
      } else {
        setLocalValue(formatWithCommas(parseFloat(value).toFixed(2)));
      }
    }
  }, [value]);

  const handleFocus = (e) => e.target.select();

  const handleChange = (e) => {
    let raw = e.target.value.replace(/,/g, "");
    if (!/^\d*\.?\d*$/.test(raw)) return;
    const parts = raw.split(".");
    if (parts[1]?.length > 2) return;
    let displayVal = raw;
    if (parts[0].length > 3) displayVal = formatWithCommas(raw);
    setLocalValue(displayVal);
    const numericValue = parseFloat(raw) || 0;
    onValueChange(numericValue);
  };

  const handleBlur = () => {
    const numericValue = parseFloat(localValue.replace(/,/g, "")) || 0;
    const formatted = formatWithCommas(numericValue.toFixed(2));
    setLocalValue(formatted);
    onValueChange(numericValue);
  };

  return (
    <input
      ref={inputRef}
      type="text"
      value={localValue}
      onFocus={handleFocus}
      onChange={handleChange}
      onBlur={handleBlur}
      className="w-full h-[42px] border border-gray-300 rounded-md px-3 py-1 text-right text-[14px] focus:ring-1 focus:ring-[#E13F57] focus:border-[#E13F57] transition"
      inputMode="decimal"
    />
  );
};

export default function QuoteItemsTable({
  items = [],
  handleChange,
  handleDelete,
  onAddItem,
  header,
  setHeader,
  disabled,
  maxItems,
  hasSubLocations = false,
}) {
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(5);

  const netTotal = items.reduce((sum, i) => {
    const qty = i.Item_Qty ?? 1;
    const price = i.Item_Price_Unit ?? 0;
    const discount = i.Item_Discount ?? 0;
    return sum + (qty * price - discount);
  }, 0);

  const vatAmount =
    header.IsVat === 1
      ? netTotal * 0.07
      : header.IsVatIn === 1
      ? netTotal - netTotal / 1.07
      : 0;

  const grandTotal = header.IsVat === 1 ? netTotal + vatAmount : netTotal;

  const totalItems = items.length;
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedData = items.slice(startIndex, startIndex + itemsPerPage);

  return (
    <div className="bg-white rounded-xl shadow-md p-6 mt-6 border border-gray-300 text-[14px]">
      <div className="flex justify-between items-center mb-4">
        <div className="flex items-center gap-3">
          <h3 className="font-bold text-gray-700 text-lg">รายการสินค้า / บริการ</h3>

          {/* ซ่อน checkbox ภาษีมูลค่าเพิ่ม */}
        </div>

        <button
          onClick={() => {
            if (maxItems != null && items.length >= maxItems) {
              Swal.fire("ถึงจำนวนรายการสูงสุดแล้ว", "", "info");
              return;
            }
            onAddItem?.();
          }}
          disabled={disabled || (maxItems != null && items.length >= maxItems)}
          className="flex items-center gap-1 text-[#F17F25] text-sm font-semibold border-b-2 border-[#F17F25] pb-0.5 hover:text-[#c7641c] transition disabled:opacity-50 disabled:cursor-not-allowed disabled:border-gray-300 disabled:text-gray-400"
        >
          <Icon icon="mdi:plus-circle-outline" className="w-5 h-5" />
          เพิ่มสินค้า/บริการ
        </button>

      </div>

      {/*  ตารางแบบ ContractPaymentTable */}
      <div className="overflow-x-auto rounded-lg border border-gray-300">
        <table className="table-fixed w-full border border-gray-300 text-[14px]">
          <thead>
            <tr>
              <th className="bg-[#E13F57] text-white text-center border border-gray-300 w-[6%] py-2">
                ลำดับ
              </th>
              <th className="bg-[#E13F57] text-white text-center border border-gray-300 w-[10%] py-2">
                รูป
              </th>
              <th className={`bg-[#E13F57] text-white border border-gray-300 py-2 ${hasSubLocations ? "text-left w-[64%]" : "text-left w-[28%]"}`}>
                ชื่อสินค้า / บริการ
              </th>
              {!hasSubLocations && (
                <>
                  <th className="bg-[#E13F57] text-white text-center border border-gray-300 w-[7%] py-2">
                    จำนวน
                  </th>
                  <th className="bg-[#E13F57] text-white text-center border border-gray-300 w-[13%] py-2">
                    ราคาต่อหน่วย
                  </th>
                  <th className="bg-[#E13F57] text-white text-center border border-gray-300 w-[10%] py-2">
                    ส่วนลด
                  </th>
                  {/* ซ่อน col VAT (7%) */}
                  <th className="bg-[#E13F57] text-white text-center border border-gray-300 w-[12%] py-2">
                    รวม
                  </th>
                </>
              )}
              <th className="bg-[#E13F57] text-white text-center border border-gray-300 w-[10%] py-2">
                ลบ
              </th>
            </tr>
          </thead>

          <tbody>
            {paginatedData.length > 0 ? (
              paginatedData.map((item, idx) => {
                const rowIndex = startIndex + idx;
                const unitPrice = item.Item_Price_Unit ?? item.Pack_Price ?? 0;
                const qty = item.Item_Qty ?? 1;
                const discount = item.Item_Discount ?? 0;
                const baseAmount = qty * unitPrice - discount;
                const total = baseAmount;

                let imgSrc = null;
                try {
                  if (item.Item_Img_Url) {
                    imgSrc = item.Item_Img_Url;
                  } else if (item.Item_Img?.data) {
                    const blob = new Blob([Uint8Array.from(item.Item_Img.data)], {
                      type: "image/*",
                    });
                    imgSrc = URL.createObjectURL(blob);
                  }
                } catch {}

                return (
                  <tr key={rowIndex} className="odd:bg-white even:bg-gray-50 hover:bg-gray-100">
                    <td className="text-center border border-gray-300 py-2">{rowIndex + 1}</td>

                    <td className="text-center border border-gray-300 py-2">
                      {imgSrc ? (
                        <img
                          src={imgSrc}
                          alt="img"
                          className="w-[50px] h-[50px] object-cover rounded-md mx-auto"
                        />
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>

                    <td className="border border-gray-300 px-2 font-medium">
                      {item.Pack_Name || item.Item_Name}
                    </td>

                    {!hasSubLocations && (
                      <>
                        <td className="border border-gray-300 px-1">
                          <IntegerInput
                            value={qty}
                            onValueChange={(v) => handleChange(rowIndex, "Item_Qty", v)}
                          />
                        </td>
                        <td className="border border-gray-300 px-1">
                          <NumberInput
                            value={unitPrice}
                            onValueChange={(v) => handleChange(rowIndex, "Item_Price_Unit", v)}
                          />
                        </td>
                        <td className="border border-gray-300 px-1">
                          <NumberInput
                            value={discount}
                            onValueChange={(v) => handleChange(rowIndex, "Item_Discount", v)}
                          />
                        </td>
                        {/* ซ่อน col VAT (7%) */}
                        <td className="text-right border border-gray-300 px-2 font-semibold">
                          {formatWithCommas(total.toFixed(2))}
                        </td>
                      </>
                    )}

                    <td className="text-center border border-gray-300">
                      <button onClick={() => handleDelete(rowIndex, item)}>
                        <Icon
                          icon="mdi:trash-can"
                          className="w-5 h-5 text-red-500 hover:text-red-700"
                        />
                      </button>
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td
                  colSpan={hasSubLocations ? 4 : 8}
                  className="text-center py-4 text-gray-400 border border-gray-300"
                >
                  ยังไม่มีสินค้า/บริการที่เลือก
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <Pagination
        totalItems={totalItems}
        itemsPerPage={itemsPerPage}
        currentPage={currentPage}
        onPageChange={setCurrentPage}
        onItemsPerPageChange={(val) => {
          setItemsPerPage(val);
          setCurrentPage(1);
        }}
      />

      {/* ===== VAT Summary ===== */}
      {!hasSubLocations && (
        <div className="flex justify-end mt-4">
          <div className="w-72 space-y-2 text-[14px]">
            <div className="flex justify-between items-center py-1">
              <span className="text-gray-600">ราคารวม</span>
              <span className="font-medium">{formatWithCommas(netTotal.toFixed(2))}</span>
            </div>

            <div className="flex gap-6 items-center py-1 border-t border-gray-200">
              <label className={`flex items-center gap-1.5 ${disabled ? "cursor-not-allowed opacity-60" : "cursor-pointer"}`}>
                <input
                  type="checkbox"
                  checked={header.IsVat === 1}
                  disabled={disabled}
                  onChange={() => {
                    if (disabled) return;
                    setHeader((prev) => ({
                      ...prev,
                      IsVat: prev.IsVat === 1 ? 0 : 1,
                      IsVatIn: 0,
                    }));
                  }}
                  className="h-4 w-4 accent-[#E13F57]"
                />
                <span className="text-gray-700">VAT <span className="text-gray-400 text-xs">(+7%)</span></span>
              </label>
              <label className={`flex items-center gap-1.5 ${disabled ? "cursor-not-allowed opacity-60" : "cursor-pointer"}`}>
                <input
                  type="checkbox"
                  checked={header.IsVatIn === 1}
                  disabled={disabled}
                  onChange={() => {
                    if (disabled) return;
                    setHeader((prev) => ({
                      ...prev,
                      IsVatIn: prev.IsVatIn === 1 ? 0 : 1,
                      IsVat: 0,
                    }));
                  }}
                  className="h-4 w-4 accent-[#E13F57]"
                />
                <span className="text-gray-700">VAT in</span>
              </label>
            </div>

            {(header.IsVat === 1 || header.IsVatIn === 1) && (
              <div className="flex justify-between items-center text-gray-500 py-1">
                <span>ภาษีมูลค่าเพิ่ม (7%)</span>
                <span>{formatWithCommas(vatAmount.toFixed(2))}</span>
              </div>
            )}

            <div className="flex justify-between items-center font-bold text-[15px] border-t-2 border-gray-300 pt-2">
              <span>รวมทั้งสิ้น</span>
              <span className="text-[#E13F57]">{formatWithCommas(grandTotal.toFixed(2))}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
